import { CURRENCY, MARKET, RESEARCH, TECHS, type TechId } from '@sd/content';
import { researchBlocker, researchSlots } from '@sd/sim';
import { canBuy, wellFed } from './fleets';
import type { Bot } from './match';

/**
 * The order a simple bot researches in: economy first, then trade and
 * exploration, defence late. Personalities research in their own order.
 */
export const BALANCED_RESEARCH: readonly TechId[] = [
  'appliedScience',
  'automation',
  'agrogenetics',
  'tradeProtocols',
  'deepMining',
  'scannerMk2',
  'fusionReactors',
  'orbitalInfrastructure',
  'financialEngineering',
  'xenomaterials',
  'defenseSystems',
  'stellarMeteorology',
  'fleetLogistics',
  'nanofactories',
  'interstellarFinance',
  'scannerMk3',
  'starControl',
  'magneticScreens',
  'heavyHulls',
  'orbitalAssault',
];

/** How keen a researcher is to pay for tier 3 with exotics. */
export interface ResearchStyle {
  /** Exotics kept aside on top of a technology's price. */
  readonly exoticsKept: number;
  /** Buys exotics for research only while the treasury holds this many times their price. */
  readonly treasuryMultiple: number;
  /** Researches only the first this many technologies of its order until they are all known. */
  readonly focus?: number;
}

const CAUTIOUS: ResearchStyle = { exoticsKept: 10, treasuryMultiple: 4 };

/**
 * Phase 2 researcher (GDD §10): keeps every research slot busy, in a fixed
 * order. When the next technology waits only for exotics and the science for
 * it is saved up, it buys the exotics on the exchange if the treasury allows.
 */
export function makeResearcher(
  order: readonly TechId[],
  { exoticsKept, treasuryMultiple, focus }: ResearchStyle = CAUTIOUS,
): Bot {
  return (state, playerId, rng) => {
    if (state.pause || !rng.chance(0.05)) return [];
    const me = state.players.find((p) => p.id === playerId);
    if (!me || me.research.active.length >= researchSlots(me.planet)) return [];
    // A focused researcher saves its science for its road until the road is done.
    const road = focus ? order.slice(0, focus) : order;
    const list = road.every((t) => me.techs.includes(t)) ? order : road;
    const open = list.filter(
      (t) => !me.research.active.includes(t) && researchBlocker(t, me.techs) === null,
    );
    const exoticsFor = (t: TechId) =>
      me.research.exoticsPaid.includes(t) ? 0 : RESEARCH.cost[TECHS[t].tier].exotics;
    const next = open.find(
      (t) => exoticsFor(t) === 0 || me.goods.exotics >= exoticsFor(t) + exoticsKept,
    );
    if (next) return [{ type: 'startResearch', playerId, tech: next }];
    const wanted = open[0];
    if (!wanted || me.planet.science < RESEARCH.cost[TECHS[wanted].tier].science / 2) return [];
    if (!wellFed(me)) return [];
    const missing = Math.ceil(exoticsFor(wanted) + exoticsKept - me.goods.exotics);
    const price = missing * state.market.exotics.price * (1 + MARKET.fee + CURRENCY.conversionFee);
    if (me.cash * me.currency.rate < price * treasuryMultiple) return [];
    if (!canBuy(state, playerId, 'exotics')) return [];
    return [{ type: 'marketBuy', playerId, good: 'exotics', quantity: missing }];
  };
}

export const researcherBot: Bot = makeResearcher(BALANCED_RESEARCH);
